import { useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useActiveContracts } from '@/hooks/contracts/useContracts';
import { useFacilities } from '@/hooks/warehouse/useWarehouse';
import { contractInvoicesQuery, STALE_TIME as INVOICES_STALE_TIME } from '@/hooks/invoices/useInvoices';
import { useAuth } from '@/auth/useAuth';
import { ContractsApi } from '@/lib/contracts-api';
import { collectQueryResults } from '@/lib/query-utils';
import { queryKeys } from '@/config/query-keys';
import type { Contract } from '@/types/contract';

export function useEnrichedContracts() {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId ?? 0;
  const queryClient = useQueryClient();

  const contracts = useActiveContracts();
  const facilities = useFacilities();

  const contractIds = useMemo(
    () => contracts.data?.items.map((contract) => contract.id) ?? [],
    [contracts.data],
  );

  // Per contract: its lines with their own money, for the service chips...
  const details = useQueries({
    queries: contractIds.map((id) => ({
      queryKey: queryKeys.contracts.lineDetails(customerId, id),
      queryFn: () => ContractsApi.getContractDetails(id),
      enabled: !!customerId,
      // Kept equal to the invoices' staleTime: a chip's debt and the card's
      // "Оплатить" total must go stale together, or a new line invoice can show
      // up in one minutes before the other.
      staleTime: INVOICES_STALE_TIME,
    })),
    combine: collectQueryResults,
  });

  // ...and its invoices, its lines' included, attributed to it server-side. A
  // rack/insurance invoice is billed against the line (its entityId is the line's
  // id), so the customer-wide invoice list can't be split per contract by id.
  const invoices = useQueries({
    queries: contractIds.map((id) => ({
      ...contractInvoicesQuery(customerId, id),
      enabled: !!customerId,
    })),
    combine: collectQueryResults,
  });

  const enrichedContracts = useMemo(() => {
    if (!contracts.data?.items) return [];

    return contracts.data.items.map((contract, index) => {
      const enriched: Contract = { ...contract };

      if (facilities.data?.items) {
        const facilityWithTypes = facilities.data.items.find(
          f => f.facility.code === contract.facility
        );
        enriched.facilityItem = facilityWithTypes?.facility;
        enriched.unitTypeItem = facilityWithTypes?.unitTypes.find(
          ut => ut.code === contract.unitType
        );
      }

      // Both query lists were built from contractIds, i.e. in this same order.
      enriched.lineDetails = details.data[index]?.lines;
      enriched.invoices = invoices.data[index]?.items;

      return enriched;
    });
  }, [contracts.data, facilities.data, details.data, invoices.data]);

  // What the cards cannot be drawn without; a line's chips failing leaves the card without them.
  const failed = contracts.isError || facilities.isError || invoices.error != null;

  const retry = () => {
    void queryClient.refetchQueries({ queryKey: queryKeys.contracts.all(customerId), type: 'active' });
    void queryClient.refetchQueries({ queryKey: queryKeys.invoices.all(customerId), type: 'active' });
    void queryClient.refetchQueries({ queryKey: queryKeys.facilities.list(), type: 'active' });
  };

  return {
    contracts: enrichedContracts,
    total: contracts.data?.total ?? 0,
    // Cards wait for their invoices (the pay button and its total), not for the chips.
    isLoading: contracts.isLoading || facilities.isLoading || invoices.isLoading,
    failed,
    retry,
    error: contracts.error || facilities.error || invoices.error || details.error,
  };
}
